import { baseApi } from "@/shared/api";
import type { DraftSectionKey, StagedFile, DeleteDraftFileDto } from "@/features/self/personal-sheet";
import type { PersonalSheet } from "@/features/self/profile/types/personalSheet";

export interface EditDraftDto {
  data: PersonalSheet;
  last_active_section: DraftSectionKey;
}

export interface EditDraftResponse {
  has_draft: boolean;
  data: PersonalSheet;
  last_active_section: DraftSectionKey;
  uploaded_files: StagedFile[];
}

export const dataChangeDraftApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getSelfDataChangeDraft: builder.query<EditDraftResponse, void>({
      query: () => ({
        url: "/api/self/personal-data-change/draft/",
        method: "GET",
        credentials: "include" as const,
      }),
      providesTags: [{ type: "PersonalDataChangeDraft" as const, id: "SELF" }],
    }),

    upsertSelfDataChangeDraft: builder.mutation<EditDraftResponse, EditDraftDto>({
      query: (body) => ({
        url: "/api/self/personal-data-change/draft/",
        method: "PUT",
        body,
        credentials: "include" as const,
      }),
    }),

    deleteSelfDataChangeDraft: builder.mutation<void, void>({
      query: () => ({
        url: "/api/self/personal-data-change/draft/",
        method: "DELETE",
        credentials: "include" as const,
      }),
      invalidatesTags: [{ type: "PersonalDataChangeDraft" as const, id: "SELF" }],
    }),

    uploadSelfDataChangeDraftFile: builder.mutation<StagedFile, FormData>({
      query: (body) => ({
        url: "/api/self/personal-data-change/draft/files/",
        method: "POST",
        body,
        credentials: "include" as const,
      }),
    }),

    deleteSelfDataChangeDraftFile: builder.mutation<void, DeleteDraftFileDto>({
      query: (body) => ({
        url: "/api/self/personal-data-change/draft/files/",
        method: "DELETE",
        body,
        credentials: "include" as const,
      }),
    }),

    getWorkerDataChangeDraft: builder.query<EditDraftResponse, number>({
      query: (workerId) => ({
        url: `/api/hr/workers/${workerId}/personal-data-change/draft/`,
        method: "GET",
        credentials: "include" as const,
      }),
      providesTags: (_result, _error, workerId) => [{ type: "PersonalDataChangeDraft" as const, id: workerId }],
    }),

    upsertWorkerDataChangeDraft: builder.mutation<EditDraftResponse, { workerId: number } & EditDraftDto>({
      query: ({ workerId, ...body }) => ({
        url: `/api/hr/workers/${workerId}/personal-data-change/draft/`,
        method: "PUT",
        body,
        credentials: "include" as const,
      }),
    }),

    deleteWorkerDataChangeDraft: builder.mutation<void, number>({
      query: (workerId) => ({
        url: `/api/hr/workers/${workerId}/personal-data-change/draft/`,
        method: "DELETE",
        credentials: "include" as const,
      }),
      invalidatesTags: (_result, _error, workerId) => [{ type: "PersonalDataChangeDraft" as const, id: workerId }],
    }),

    uploadWorkerDataChangeDraftFile: builder.mutation<StagedFile, { workerId: number; body: FormData }>({
      query: ({ workerId, body }) => ({
        url: `/api/hr/workers/${workerId}/personal-data-change/draft/files/`,
        method: "POST",
        body,
        credentials: "include" as const,
      }),
    }),

    deleteWorkerDataChangeDraftFile: builder.mutation<void, { workerId: number; body: DeleteDraftFileDto }>({
      query: ({ workerId, body }) => ({
        url: `/api/hr/workers/${workerId}/personal-data-change/draft/files/`,
        method: "DELETE",
        body,
        credentials: "include" as const,
      }),
    }),
  }),
});

export const {
  useGetSelfDataChangeDraftQuery,
  useUpsertSelfDataChangeDraftMutation,
  useDeleteSelfDataChangeDraftMutation,
  useUploadSelfDataChangeDraftFileMutation,
  useDeleteSelfDataChangeDraftFileMutation,
  useGetWorkerDataChangeDraftQuery,
  useUpsertWorkerDataChangeDraftMutation,
  useDeleteWorkerDataChangeDraftMutation,
  useUploadWorkerDataChangeDraftFileMutation,
  useDeleteWorkerDataChangeDraftFileMutation,
} = dataChangeDraftApi;
